import {
	type InstanceTask,
	type Graph,
	type KnowledgeObject,
	type GraphModification,
	UserRole,
} from "@lr-notitia/notitia-operator/schemas";
import { Ref, computed } from "vue";
import { useDomainStore } from "../domain";
import { useInstanceWorkspacesStore } from "../i-workspace";
import { api } from "@lr-notitia/notitia-operator/client";
import { handleGraphModification } from "./utils";

let domainStore: ReturnType<typeof useDomainStore>;
let instanceWorkspacesStore: ReturnType<typeof useInstanceWorkspacesStore>;
function init() {
	domainStore = useDomainStore();
	instanceWorkspacesStore = useInstanceWorkspacesStore();
}

export const instanceTasks = (graph: Ref<Graph>) =>
	computed<InstanceTask[]>(
		() =>
			Object.values(graph.value.nodes).filter(
				(node: any) => node.type === "task" && node.layer === "instance"
			) as unknown as InstanceTask[]
	);
export function instanceTask(domainId: Ref<string>, graph: Ref<Graph>) {
	return {
		byId: (id: string) => byId(id, graph),
		update: (value: any) => update(domainId, graph, value),
		markAnsweredBy: (
			instanceTask: InstanceTask,
			attributes: { instanceAttrId: string; value: any; source: string }[]
		) => markAnsweredBy(domainId, graph, instanceTask, attributes),
	};
}

function byId(id: string, graph: Ref<Graph>): InstanceTask | undefined {
	let hit = graph.value.nodes[id] as KnowledgeObject;
	return hit && hit.type === "task" && hit.layer === "instance" ? (hit as InstanceTask) : undefined;
}

function update(domainId: Ref<string>, graph: Ref<Graph>, value: InstanceTask): Promise<GraphModification> {
	if (!domainStore || !instanceWorkspacesStore) init();
	return new Promise((resolve, reject) => {
		if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
		if (!graph.value.nodes[value.id]) return reject();
		if (!instanceWorkspacesStore.openedWorkspaceId) return reject();
		api()
			.domain(domainId.value)
			.workspace(instanceWorkspacesStore.openedWorkspaceId)
			.task(value.id)
			.put(value)
			.then((data) => {
				handleGraphModification(data.modification, data.metadata);
				resolve(data.modification);
			})
			.catch(() => reject());
	});
}

function markAnsweredBy(
	domainId: Ref<string>,
	graph: Ref<Graph>,
	instanceTask: InstanceTask,
	attributes: { instanceAttrId: string; value: any; source: string }[]
): Promise<GraphModification> {
	if (!instanceTask.context) instanceTask.context = {};
	if (typeof instanceTask.context.answeredBy !== "object" || instanceTask.context.answeredBy === null) {
		instanceTask.context.answeredBy = {};
	}

	attributes.forEach(({ instanceAttrId, value, source }) => {
		if (!Array.isArray(instanceTask.context!.answeredBy[instanceAttrId])) {
			instanceTask.context!.answeredBy[instanceAttrId] = [];
		}
		instanceTask.context!.answeredBy[instanceAttrId].push({
			source,
			timestamp: Date.now(),
			value,
		});
	});

	return update(domainId, graph, instanceTask);
}
